import ChevronRight from '@/assets/icons/ChevronRight.svg';
import {
  FloatingNextArrow,
  FloatingPrevArrow,
} from '@/components/CarouselControls';
import { useDataLayer } from '@/hooks/useDataLayer';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useTrackPromotion } from '@/hooks/useTrackPromotion';
import type { Ga4Promotion } from '@/types';
import { buildCloudflareImageUrl } from '@/utils/buildCloudflareImageUrl';
import { slugify } from '@/utils/slugify';
import { useMemo, useRef } from 'react';
import { CarouselItem } from 'react-aria-carousel';
import * as Styled from './BrandCarousel.styled';
import type { BrandCarouselData, BrandCarouselTile } from './types';

const CREATIVE_SLOT = 'brand-carousel';

// A tile with no label has no stable name/id, so it can't be a tracked
// promotion — fall back to a positional id and let the caller skip it.
const tilePromotion = (
  tile: BrandCarouselTile,
  index: number,
  heading?: string,
): Ga4Promotion | null => {
  if (!tile.label) {
    return null;
  }
  return {
    promotion_id: slugify(tile.label) || `${CREATIVE_SLOT}-${index}`,
    promotion_name: tile.label,
    creative_name: heading || CREATIVE_SLOT,
    creative_slot: CREATIVE_SLOT,
    index,
  };
};

const Tile = ({
  tile,
  promotion,
}: {
  tile: BrandCarouselTile;
  promotion: Ga4Promotion | null;
}) => {
  const tileRef = useRef<HTMLElement>(null);
  const { onSelect } = useTrackPromotion(
    tileRef,
    promotion ? [promotion] : [],
  );

  if (!tile.imageUrl) {
    return null;
  }

  // Distinguish which part of the tile was clicked via `creative_name`.
  const select = (kind: 'tile' | 'cta') => {
    if (promotion) {
      onSelect({
        ...promotion,
        creative_name: `${promotion.creative_name} · ${kind}`,
      });
    }
  };

  const img = (
    <Styled.ImageBox>
      <Styled.Img
        src={buildCloudflareImageUrl(tile.imageUrl, { width: 600 })}
        alt={tile.altText && tile.altText !== tile.label ? tile.altText : ''}
        loading="lazy"
        decoding="async"
      />
    </Styled.ImageBox>
  );

  const label = tile.label ? <Styled.Label>{tile.label}</Styled.Label> : null;
  const surface = tile.surface || 'surface-white';

  if (tile.linkStyle === 'rangeLink' && tile.linkUrl) {
    return (
      <Styled.CardLink
        ref={tileRef as React.RefObject<HTMLAnchorElement>}
        className={surface}
        href={tile.linkUrl}
        onClick={() => select('tile')}
      >
        {img}
        {label}
        {tile.linkText ? (
          <Styled.LinkRow>
            <span>{tile.linkText}</span>
            <ChevronRight aria-hidden="true" />
          </Styled.LinkRow>
        ) : null}
      </Styled.CardLink>
    );
  }

  if (tile.linkStyle === 'button' && tile.linkUrl) {
    return (
      <Styled.CardStatic
        ref={tileRef as React.RefObject<HTMLDivElement>}
        className={surface}
      >
        {img}
        {label}
        <Styled.CtaButton href={tile.linkUrl} onClick={() => select('cta')}>
          {tile.linkText ?? 'Explore the full range'}
        </Styled.CtaButton>
      </Styled.CardStatic>
    );
  }

  return (
    <Styled.CardStatic
      ref={tileRef as React.RefObject<HTMLDivElement>}
      className={surface}
    >
      {img}
      {label}
    </Styled.CardStatic>
  );
};

export const BrandCarousel = ({ data }: { data: BrandCarouselData }) => {
  const isWideScreen = useMediaQuery('(width >= 64rem)');

  const tiles = data.tiles ?? [];

  // One promotion per tile (null for label-less tiles, which aren't tracked).
  const promotions = useMemo(
    () => tiles.map((tile, index) => tilePromotion(tile, index, data.heading)),
    [tiles, data.heading],
  );

  const { mediaInteract } = useDataLayer();

  const capacity = isWideScreen ? 5 : 2.2;
  const itemsPerPage = Math.min(Math.max(tiles.length, 3), capacity);
  const enableScrolling = tiles.length > capacity;
  const showArrows = isWideScreen && enableScrolling;

  if (tiles.length === 0) {
    return null;
  }

  return (
    <Styled.Root>
      <Styled.Carousel
        key={data.key}
        aria-label={data.heading ? `Brands: ${data.heading}` : 'Brand carousel'}
        itemsPerPage={itemsPerPage}
        onActivePageIndexChange={({ index }) =>
          mediaInteract('brand-carousel', 'navigate', index)
        }
        scrollPadding="5px"
        spaceBetweenItems={isWideScreen ? '12px' : '14px'}
      >
        <Styled.Track $scroll={enableScrolling}>
          {tiles.map((tile, index) => (
            <CarouselItem key={`${data.key}-${index}`}>
              <Tile tile={tile} promotion={promotions[index]} />
            </CarouselItem>
          ))}
        </Styled.Track>

        {showArrows ? (
          <>
            <FloatingPrevArrow />
            <FloatingNextArrow />
          </>
        ) : null}
      </Styled.Carousel>
    </Styled.Root>
  );
};
